import { useEffect } from "react";
import Tabs from "@/components/Tabs";
import { useDenyAuthorization } from "@/hooks/useCapabilities";
import AppLayout from "@/layouts/AppLayout";
import PageMessage from "@/components/PageMessage";
import { applyBackendSource } from "@/lib/backendSource";
import { Path, useNavigate } from "@/router";
import { useLocation } from "react-router-dom";
import { useConfigurationAccess } from "./access";
import { shouldSwitchConfigurationToLocalBackend } from "./localBackend";

const TAB_ITEMS = [
  { label: "Career Consultants", to: "/configuration/career-consultants" },
  { label: "Player Engagement", to: "/configuration/player-engagement" },
  { label: "Recruiters", to: "/configuration/recruiters" }
] as const;

function ConfigurationTabs() {
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const normalizedPath = pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;

  return (
    <Tabs
      tabItems={TAB_ITEMS.map(({ label, to }) => ({
        label,
        onClick: () => navigate(to as Path),
        active: normalizedPath === to
      }))}
    />
  );
}

export default function TabLayout({ children }: { children: React.ReactNode }) {
  const denyAuthorization = useDenyAuthorization();
  const { loaded, canView } = useConfigurationAccess();
  const switchToLocalBackend = loaded && canView && shouldSwitchConfigurationToLocalBackend();

  useEffect(() => {
    if (switchToLocalBackend) {
      applyBackendSource("localhost");
    }
  }, [switchToLocalBackend]);

  useEffect(() => {
    if (!loaded) {
      return;
    }

    if (!canView) {
      denyAuthorization();
    }
  }, [loaded, canView, denyAuthorization]);

  if (switchToLocalBackend) {
    return (
      <PageMessage
        title="Switching to your local API"
        message="Member 360 Configuration isn't on the shared-dev API yet."
      />
    );
  }

  if (!loaded || !canView) {
    return null;
  }

  return (
    <AppLayout>
      <ConfigurationTabs />
      <div className="flex flex-col">{children}</div>
    </AppLayout>
  );
}
